/**
 * EdgeStreamPageRenderer
 * Renders EdgeStream page components inside Flow Studio.
 * Wraps pages with MemoryRouter (for react-router hooks) and EdgeStreamProvider.
 *
 * All imports are resolved via Vite aliases in apps/studio/vite.config.ts —
 * no npm install of edge-stream packages is required.
 */

/* eslint-disable @typescript-eslint/ban-ts-comment */

import React, { useMemo } from 'react';
import { MemoryRouter, Routes, Route } from 'react-router-dom';

// @ts-ignore — resolved via vite alias → edge-stream/packages/edge-stream-js/src/index.ts
import { createEdgeStream } from 'edge-stream-js';
// @ts-ignore — resolved via vite alias → edge-stream/packages/edge-stream-js/src/react/index.ts
import { EdgeStreamProvider } from 'edge-stream-js/react';

// @ts-ignore — resolved via vite alias → edge-stream/packages/app-pages-react/src/index.ts
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as _appPages from '@edge-stream/app-pages-react';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { ServerDashboard, UserDashboard, ChatPage, EventViewerPage, ActivityStreamPage, PipelinesPage, DemoPage, HooksPage, HooksLibraryPage, HooksReferencePage, SubscribersReferencePage, SubscribersPage, SubscribersHelpPage, PipelineHelpPage } = _appPages as any;

import { calculateApiUrl } from '@bizfirst/common-js';

import { type ModuleItem } from '../../modules/moduleRegistry';

// Same-origin default when VITE_API_BASE_URL is unset — mirrors flow-studio's main.tsx
const DEFAULT_API_URL = calculateApiUrl((import.meta as any).env?.VITE_API_BASE_URL);
const DEFAULT_USER = 'flow-studio';

// Lazily-created shared stream instance (no server registered → pages show disconnected state)
let _stream: ReturnType<typeof createEdgeStream> | null = null;
function getStream(): ReturnType<typeof createEdgeStream> {
  if (!_stream) _stream = createEdgeStream({ logLevel: 'warn' });
  return _stream;
}

interface EdgeStreamPageRendererProps {
  item: ModuleItem;
}

// Flow Studio cyberpunk dark theme — applied inline so they override any
// ancestor data-theme="light" that body may carry.
const DARK_THEME_VARS: React.CSSProperties = {
  // Backgrounds
  '--bg-primary':   '#020617',
  '--bg-secondary': '#0f172a',
  '--bg-tertiary':  '#1e293b',
  '--bg-overlay':   'rgba(0, 0, 0, 0.7)',
  // Text
  '--text-primary':   '#f8fafc',
  '--text-secondary': '#94a3b8',
  '--text-muted':     '#64748b',
  '--text-inverse':   '#020617',
  // Borders
  '--border':       'rgba(58, 140, 69, 0.2)',
  '--border-light': 'rgba(58, 140, 69, 0.12)',
  // Accent / primary
  '--obs-primary': '#3A8C45',
  '--primary':     '#3A8C45',
  // Canonical color tokens
  '--color-bg':           '#020617',
  '--color-bg-secondary': '#0f172a',
  '--color-bg-tertiary':  '#1e293b',
  '--color-text-primary':   '#f8fafc',
  '--color-text-secondary': '#94a3b8',
  '--color-border':       'rgba(58, 140, 69, 0.2)',
  '--color-primary':      '#3A8C45',
  // Inputs
  '--input-bg':     'rgba(0, 0, 0, 0.3)',
  '--input-border': 'rgba(58, 140, 69, 0.2)',
  '--input-color':  '#f8fafc',
  '--control-bg':        '#0f172a',
  '--control-bg-nested': '#1e293b',
  '--control-bg-deep':   '#1e293b',
} as React.CSSProperties;

export const EdgeStreamPageRenderer: React.FC<EdgeStreamPageRendererProps> = ({ item }) => {
  const stream = useMemo(() => getStream(), []);

  return (
    <div
      className="dark"
      data-theme="dark"
      style={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        background: '#020617',
        color: '#f8fafc',
        ...DARK_THEME_VARS,
      }}
    >
      <EdgeStreamProvider stream={stream}>
        <MemoryRouter initialEntries={[item.route]}>
          <Routes>
            <Route path="/servers"               element={<ServerDashboard           apiBaseUrl={DEFAULT_API_URL} currentUser={DEFAULT_USER} />} />
            <Route path="/my-groups"             element={<UserDashboard             apiBaseUrl={DEFAULT_API_URL} currentUser={DEFAULT_USER} />} />
            <Route path="/chat"                  element={<ChatPage                  apiBaseUrl={DEFAULT_API_URL} currentUser={DEFAULT_USER} />} />
            <Route path="/events"                element={<EventViewerPage />} />
            <Route path="/activity-stream"       element={<ActivityStreamPage />} />
            <Route path="/pipelines"             element={<PipelinesPage />} />
            <Route path="/hooks-reference"       element={<HooksReferencePage />} />
            <Route path="/subscribers-reference" element={<SubscribersReferencePage />} />
            <Route path="/hooks"                 element={<HooksPage />} />
            <Route path="/subscribers"           element={<SubscribersPage />} />
            <Route path="/demo"                  element={<DemoPage apiBaseUrl={DEFAULT_API_URL} />} />
            <Route path="/help/hooks"            element={<HooksLibraryPage />} />
            <Route path="/help/subscribers"      element={<SubscribersHelpPage />} />
            <Route path="/help/pipelines"        element={<PipelineHelpPage />} />
          </Routes>
        </MemoryRouter>
      </EdgeStreamProvider>
    </div>
  );
};
